/**
 * Date-grouping helpers shared by every feed that renders day separators with
 * a jump-to-date affordance (any scrolling timeline). Kept free of any
 * domain types so the date-jump module stays generic.
 */

/**
 * Format a local Date as a `YYYY-MM-DD` key.
 *
 * We deliberately read the local Y/M/D rather than `toISOString()`, which
 * would shift to UTC and silently disagree with the server (which keys by the
 * viewer's local zone via `$timezone`) and with the popover's local
 * today/yesterday math for non-UTC viewers.
 */
export function formatDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Human-readable label for a day group: "Today", "Yesterday", or
 * "Wednesday, May 27" (with the year, "Wednesday, May 27, 2025", for any
 * other year). One `toLocaleDateString` call owns the whole phrase: a
 * weekday+month pair formatted alone comes back in the engine's own order
 * ("May Wednesday" in Chrome), so it cannot be glued to the day by hand.
 */
export function getDateLabel(date: Date): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const groupDate = new Date(date);
  groupDate.setHours(0, 0, 0, 0);

  if (groupDate.getTime() === today.getTime()) return "Today";
  if (groupDate.getTime() === yesterday.getTime()) return "Yesterday";

  return groupDate.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    ...(groupDate.getFullYear() !== today.getFullYear()
      ? { year: "numeric" as const }
      : {}),
  });
}
